import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Edit, Trash2, Save, X, Copy, GripVertical,
  BookOpen, Users, Heart, Sparkles, PenTool, Eye, EyeOff,
  ChevronRight, Loader2, AlertTriangle, Check, MoreVertical,
  Upload, Download, FileText
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { API_URL } from '../utils/api';

const CATEGORIES = [
  { id: 'SEL', label: 'SEL (Social-Emotional Learning)', icon: Heart, color: 'text-pink-400' },
  { id: 'Academic', label: 'Academic', icon: BookOpen, color: 'text-blue-400' },
  { id: 'Therapy', label: 'Therapy', icon: Users, color: 'text-green-400' },
  { id: 'Creative Writing', label: 'Creative Writing', icon: PenTool, color: 'text-purple-400' },
  { id: 'Custom', label: 'Custom', icon: Sparkles, color: 'text-yellow-400' }
];

const AUDIENCES = [
  { id: 'all', label: 'All Ages' },
  { id: 'elementary', label: 'Elementary (K-5)' },
  { id: 'middle_school', label: 'Middle School (6-8)' },
  { id: 'high_school', label: 'High School (9-12)' },
  { id: 'adult', label: 'Adult' }
];

const TOPICS = [
  { id: 'self_discovery', label: 'Self Discovery' },
  { id: 'goals_dreams', label: 'Goals & Dreams' },
  { id: 'relationships', label: 'Relationships' },
  { id: 'challenges', label: 'Challenges & Growth' },
  { id: 'gratitude', label: 'Gratitude' },
  { id: 'identity', label: 'Identity & Culture' },
  { id: 'emotions', label: 'Emotions & Feelings' },
  { id: 'community', label: 'Community & Belonging' },
  { id: 'creativity', label: 'Creativity & Expression' },
  { id: 'custom', label: 'Custom Topic' }
];

// Sortable Question Item Component
const SortableQuestion = ({ question, onEdit, onDelete, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.question_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${
        question.is_active 
          ? 'bg-white/5 border-white/10' 
          : 'bg-white/2 border-white/5 opacity-60'
      }`}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="mt-1 p-1 rounded hover:bg-white/10 cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </button>
      
      {/* Question Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 mb-1">
          <span className="text-xs text-muted-foreground font-mono">
            Q{question.order_index + 1}
          </span>
          {question.is_required && (
            <Badge className="bg-red-500/20 text-red-400 border-0 text-[10px]">Required</Badge>
          )}
          {!question.is_active && (
            <Badge className="bg-white/10 text-muted-foreground border-0 text-[10px]">Inactive</Badge>
          )}
        </div>
        <p className="font-medium mb-1">{question.question_text}</p>
        {question.helper_text && (
          <p className="text-sm text-muted-foreground">💡 {question.helper_text}</p>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggle(question)}
          className="h-8 w-8 p-0"
          title={question.is_active ? 'Deactivate' : 'Activate'}
        >
          {question.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(question)}
          className="h-8 w-8 p-0"
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(question)}
          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const AssessmentThemeManager = () => {
  const { theme_id } = useParams();
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Theme List State
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [themeToDelete, setThemeToDelete] = useState(null);
  
  // Theme Form State
  const [formData, setFormData] = useState({
    theme_name: '',
    description: '',
    category: 'SEL',
    audience: 'all',
    topic: '',
    is_active: true,
    is_default: false
  });
  
  // Bulk Import State
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = React.useRef(null);
  
  // Question Editor State (when viewing a theme)
  const [currentTheme, setCurrentTheme] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    helper_text: '',
    example_answer: '',
    is_required: true,
    is_active: true
  });
  
  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Auth check
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, isAdmin, authLoading, navigate]);
  
  // Fetch themes
  const fetchThemes = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/assessment-themes?include_inactive=true`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setThemes(data);
      }
    } catch (error) {
      console.error('Failed to fetch themes:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch single theme with questions
  const fetchTheme = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/assessment-themes/${id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentTheme(data);
        setQuestions(data.questions || []);
      } else {
        toast.error('Theme not found');
        navigate('/admin/assessment-themes');
      }
    } catch (error) {
      console.error('Failed to fetch theme:', error);
      toast.error('Failed to load theme');
    } finally {
      setLoading(false);
    }
  }, [navigate]);
  
  useEffect(() => {
    if (theme_id) {
      fetchTheme(theme_id);
    } else {
      fetchThemes();
    }
  }, [theme_id, fetchTheme, fetchThemes]);
  
  // Create theme
  const createTheme = async () => {
    if (!formData.theme_name.trim()) {
      toast.error('Theme name is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/assessment-themes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Theme created');
        setShowCreateDialog(false);
        setFormData({ theme_name: '', description: '', category: 'SEL', is_active: true, is_default: false });
        // Navigate to the new theme to add questions
        navigate(`/admin/assessment-themes/${data.theme_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create theme');
      }
    } catch (error) {
      toast.error('Error creating theme');
    }
  };
  
  // Update theme
  const updateTheme = async (updates) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/assessment-themes/${theme_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        toast.success('Theme updated');
        fetchTheme(theme_id);
      } else {
        toast.error('Failed to update theme');
      }
    } catch (error) {
      toast.error('Error updating theme');
    }
  };
  
  // Duplicate theme
  const duplicateTheme = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/assessment-themes/${id}/duplicate`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Theme duplicated');
        navigate(`/admin/assessment-themes/${data.new_theme_id}`);
      } else {
        toast.error('Failed to duplicate theme');
      }
    } catch (error) {
      toast.error('Error duplicating theme');
    }
  };
  
  // Delete theme
  const deleteTheme = async () => {
    if (!themeToDelete) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/assessment-themes/${themeToDelete.theme_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.deactivated) {
          toast.success('Theme deactivated (has existing sessions)');
        } else {
          toast.success('Theme deleted');
        }
        setShowDeleteDialog(false);
        setThemeToDelete(null);
        fetchThemes();
      } else {
        toast.error('Failed to delete theme');
      }
    } catch (error) {
      toast.error('Error deleting theme');
    }
  };
  
  // Add question
  const addQuestion = async () => {
    if (!questionForm.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/assessment-themes/${theme_id}/questions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(questionForm)
      });
      
      if (response.ok) {
        toast.success('Question added');
        setShowQuestionDialog(false);
        setQuestionForm({ question_text: '', helper_text: '', example_answer: '', is_required: true, is_active: true });
        fetchTheme(theme_id);
      } else {
        toast.error('Failed to add question');
      }
    } catch (error) {
      toast.error('Error adding question');
    }
  };
  
  // Update question
  const updateQuestion = async () => {
    if (!editingQuestion) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/assessment-themes/${theme_id}/questions/${editingQuestion.question_id}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(questionForm)
        }
      );
      
      if (response.ok) {
        toast.success('Question updated');
        setShowQuestionDialog(false);
        setEditingQuestion(null);
        setQuestionForm({ question_text: '', helper_text: '', example_answer: '', is_required: true, is_active: true });
        fetchTheme(theme_id);
      } else {
        toast.error('Failed to update question');
      }
    } catch (error) {
      toast.error('Error updating question');
    }
  };
  
  // Delete question
  const deleteQuestion = async (question) => {
    if (!confirm('Delete this question?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/assessment-themes/${theme_id}/questions/${question.question_id}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        toast.success('Question deleted');
        fetchTheme(theme_id);
      } else {
        toast.error('Failed to delete question');
      }
    } catch (error) {
      toast.error('Error deleting question');
    }
  };
  
  // Toggle question active status
  const toggleQuestion = async (question) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/assessment-themes/${theme_id}/questions/${question.question_id}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_active: !question.is_active })
        }
      );
      
      if (response.ok) {
        toast.success(question.is_active ? 'Question deactivated' : 'Question activated');
        fetchTheme(theme_id);
      }
    } catch (error) {
      toast.error('Error toggling question');
    }
  };
  
  // Handle drag end for reordering
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = questions.findIndex(q => q.question_id === active.id);
      const newIndex = questions.findIndex(q => q.question_id === over.id);
      
      const newOrder = arrayMove(questions, oldIndex, newIndex);
      setQuestions(newOrder);
      
      // Save new order to backend
      try {
        await fetch(`${API_URL}/api/admin/assessment-themes/${theme_id}/questions/reorder`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            question_ids: newOrder.map(q => q.question_id)
          })
        });
        toast.success('Questions reordered');
      } catch (error) {
        toast.error('Failed to save order');
        fetchTheme(theme_id);
      }
    }
  };
  
  // Open edit dialog
  const openEditQuestion = (question) => {
    setEditingQuestion(question);
    setQuestionForm({
      question_text: question.question_text,
      helper_text: question.helper_text || '',
      example_answer: question.example_answer || '',
      is_required: question.is_required,
      is_active: question.is_active
    });
    setShowQuestionDialog(true);
  };
  
  // Bulk import questions from CSV
  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setImporting(true);
    const loadingToast = toast.loading('Importing questions...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/admin/assessment-themes/${theme_id}/questions/bulk-import`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      toast.dismiss(loadingToast);
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Imported ${data.imported} questions`);
        if (data.errors?.length > 0) {
          toast.warning(`${data.errors.length} rows had errors`);
        }
        setShowImportDialog(false);
        fetchTheme(theme_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Import failed');
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Error importing questions');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Export questions as CSV
  const exportQuestions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/assessment-themes/${theme_id}/questions/export`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${currentTheme?.theme_name || 'theme'}_questions.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Questions exported');
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      toast.error('Error exporting questions');
    }
  };
  
  // Download CSV template
  const downloadTemplate = () => {
    const template = `question_text,helper_text,example_answer,is_required
"What makes you unique?","Think about your talents and interests","I love music and art",true
"Who inspires you?","Consider family, friends, or public figures","My grandmother inspires me",true`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'questions_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };
  
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // Theme Detail View
  if (theme_id && currentTheme) {
    const categoryInfo = CATEGORIES.find(c => c.id === currentTheme.category) || CATEGORIES[0];
    const CategoryIcon = categoryInfo.icon;
    const audienceLabel = AUDIENCES.find(a => a.id === currentTheme.audience)?.label || 'All Ages';
    const topicLabel = TOPICS.find(t => t.id === currentTheme.topic)?.label || currentTheme.topic;
    
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-white/10 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-5xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Link to="/admin/assessment-themes">
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                </Link>
                <div>
                  <div className="flex items-center gap-2">
                    <CategoryIcon className={`w-5 h-5 ${categoryInfo.color}`} />
                    <h1 className="font-heading font-bold text-xl">{currentTheme.theme_name}</h1>
                    {currentTheme.is_default && (
                      <Badge className="bg-primary/20 text-primary border-0 text-xs">Default</Badge>
                    )}
                    {!currentTheme.is_active && (
                      <Badge className="bg-white/10 text-muted-foreground border-0 text-xs">Inactive</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {currentTheme.description && <span>{currentTheme.description}</span>}
                    {currentTheme.description && (currentTheme.audience !== 'all' || currentTheme.topic) && <span>•</span>}
                    {currentTheme.audience !== 'all' && (
                      <Badge className="bg-blue-500/20 text-blue-400 border-0 text-xs">{audienceLabel}</Badge>
                    )}
                    {currentTheme.topic && (
                      <Badge className="bg-purple-500/20 text-purple-400 border-0 text-xs">{topicLabel}</Badge>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => duplicateTheme(theme_id)}
                  className="rounded-full"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Duplicate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateTheme({ is_active: !currentTheme.is_active })}
                  className="rounded-full"
                >
                  {currentTheme.is_active ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                  {currentTheme.is_active ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            </div>
          </div>
        </header>
        
        {/* Import/Export Toolbar */}
        <div className="max-w-5xl mx-auto px-4 pt-4">
          <div className="flex items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/10">
            <span className="text-sm text-muted-foreground mr-2">Bulk Actions:</span>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
              className="rounded-full text-xs"
              data-testid="download-template-btn"
            >
              <FileText className="w-3.5 h-3.5 mr-1" />
              Download Template
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowImportDialog(true)}
              className="rounded-full text-xs"
              data-testid="import-questions-btn"
            >
              <Upload className="w-3.5 h-3.5 mr-1" />
              Import CSV
            </Button>
            {questions.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={exportQuestions}
                className="rounded-full text-xs"
                data-testid="export-questions-btn"
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                Export Questions
              </Button>
            )}
          </div>
        </div>
        
        {/* Question Builder */}
        <main className="max-w-5xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-semibold text-lg">Questions</h2>
              <p className="text-sm text-muted-foreground">
                {questions.length} question{questions.length !== 1 ? 's' : ''} • Drag to reorder • No limit
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingQuestion(null);
                setQuestionForm({ question_text: '', helper_text: '', example_answer: '', is_required: true, is_active: true });
                setShowQuestionDialog(true);
              }}
              className="rounded-full bg-primary text-black"
              data-testid="add-question-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Question
            </Button>
          </div>
          
          {/* Questions List with Drag & Drop */}
          {questions.length === 0 ? (
            <div className="text-center py-16 rounded-2xl border border-dashed border-white/20">
              <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-semibold mb-2">No questions yet</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Add questions for students to answer during reflection
              </p>
              <Button
                onClick={() => setShowQuestionDialog(true)}
                className="rounded-full bg-primary text-black"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add First Question
              </Button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={questions.map(q => q.question_id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {questions.map((question, idx) => (
                    <SortableQuestion
                      key={question.question_id}
                      question={{ ...question, order_index: idx }}
                      onEdit={openEditQuestion}
                      onDelete={deleteQuestion}
                      onToggle={toggleQuestion}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
          
          {/* Preview Section */}
          {questions.length > 0 && (
            <div className="mt-8 p-6 rounded-2xl border border-white/10 bg-white/5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Student Preview
              </h3>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {questions.filter(q => q.is_active).map((q, idx) => (
                  <div key={q.question_id} className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <p className="font-medium mb-1">
                      {idx + 1}. {q.question_text}
                      {q.is_required && <span className="text-red-400 ml-1">*</span>}
                    </p>
                    {q.helper_text && (
                      <p className="text-sm text-muted-foreground">💡 {q.helper_text}</p>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                {questions.filter(q => q.is_active).length} active questions will be shown to students
              </p>
            </div>
          )}
        </main>
        
        {/* Question Dialog */}
        <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add Question'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Question Text <span className="text-red-400">*</span>
                </label>
                <Textarea
                  value={questionForm.question_text}
                  onChange={(e) => setQuestionForm(prev => ({ ...prev, question_text: e.target.value }))}
                  placeholder="What makes you unique?"
                  className="rounded-lg resize-none"
                  rows={3}
                  data-testid="question-text-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Helper Text (optional)</label>
                <Input
                  value={questionForm.helper_text}
                  onChange={(e) => setQuestionForm(prev => ({ ...prev, helper_text: e.target.value }))}
                  placeholder="Think about your talents, interests, and personality..."
                  className="rounded-lg"
                />
                <p className="text-xs text-muted-foreground mt-1">Shown as a hint below the question</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Example Answer (optional)</label>
                <Input
                  value={questionForm.example_answer}
                  onChange={(e) => setQuestionForm(prev => ({ ...prev, example_answer: e.target.value }))}
                  placeholder="I'm creative and love making music..."
                  className="rounded-lg"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={questionForm.is_required}
                    onCheckedChange={(v) => setQuestionForm(prev => ({ ...prev, is_required: v }))}
                  />
                  <label className="text-sm">Required</label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={questionForm.is_active}
                    onCheckedChange={(v) => setQuestionForm(prev => ({ ...prev, is_active: v }))}
                  />
                  <label className="text-sm">Active</label>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowQuestionDialog(false)} className="rounded-full">
                Cancel
              </Button>
              <Button
                onClick={editingQuestion ? updateQuestion : addQuestion}
                className="rounded-full bg-primary text-black"
                data-testid="save-question-btn"
              >
                {editingQuestion ? 'Save Changes' : 'Add Question'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Import Questions Dialog */}
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Import Questions from CSV
              </DialogTitle>
              <DialogDescription>
                Upload a CSV file to bulk-add questions to this theme.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* CSV Format Help */}
              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                <h4 className="text-sm font-medium text-blue-400 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  CSV Format Requirements
                </h4>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p className="font-mono bg-white/5 p-2 rounded text-[11px] overflow-x-auto">
                    question_text,helper_text,example_answer,is_required
                  </p>
                  <ul className="list-disc list-inside space-y-1 mt-2">
                    <li><span className="text-white/80">question_text</span> — Required. The question to ask students.</li>
                    <li><span className="text-white/80">helper_text</span> — Optional. A hint shown below the question.</li>
                    <li><span className="text-white/80">example_answer</span> — Optional. Example response for reference.</li>
                    <li><span className="text-white/80">is_required</span> — Optional. Use "true" or "false" (defaults to true).</li>
                  </ul>
                </div>
                <Button
                  variant="link"
                  size="sm"
                  onClick={downloadTemplate}
                  className="text-blue-400 p-0 h-auto mt-2 text-xs"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download sample template
                </Button>
              </div>
              
              {/* File Input */}
              <div>
                <label className="block text-sm font-medium mb-2">Select CSV File</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileImport}
                  className="block w-full text-sm text-muted-foreground
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-medium
                    file:bg-primary file:text-black
                    hover:file:bg-primary/80
                    file:cursor-pointer cursor-pointer"
                  data-testid="csv-file-input"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Questions will be added to the end of the existing list.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="ghost" 
                onClick={() => setShowImportDialog(false)} 
                className="rounded-full"
                disabled={importing}
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  
  // Theme List View
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/10 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="icon" className="rounded-full">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading font-bold text-xl">Assessment Themes</h1>
                <p className="text-sm text-muted-foreground">Manage reflection themes and questions</p>
              </div>
            </div>
            
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="rounded-full bg-primary text-black"
              data-testid="create-theme-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Theme
            </Button>
          </div>
        </div>
      </header>
      
      {/* Theme Grid */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {themes.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Assessment Themes Yet</h2>
            <p className="text-muted-foreground mb-4">Create your first theme to start building reflection experiences</p>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="rounded-full bg-primary text-black"
            >
              <Plus className="w-4 h-4 mr-1" />
              Create First Theme
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {themes.map(theme => {
              const categoryInfo = CATEGORIES.find(c => c.id === theme.category) || CATEGORIES[0];
              const CategoryIcon = categoryInfo.icon;
              
              return (
                <div
                  key={theme.theme_id}
                  className={`rounded-2xl border p-5 transition-all hover:border-white/20 ${
                    theme.is_active 
                      ? 'bg-white/5 border-white/10' 
                      : 'bg-white/2 border-white/5 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2 rounded-xl ${categoryInfo.color} bg-white/10`}>
                      <CategoryIcon className="w-5 h-5" />
                    </div>
                    <div className="flex items-center gap-1">
                      {theme.is_default && (
                        <Badge className="bg-primary/20 text-primary border-0 text-xs">Default</Badge>
                      )}
                      {!theme.is_active && (
                        <Badge className="bg-white/10 text-muted-foreground border-0 text-xs">Inactive</Badge>
                      )}
                    </div>
                  </div>
                  
                  <h3 className="font-semibold mb-1">{theme.theme_name}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {theme.description || 'No description'}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {theme.question_count || 0} questions
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => duplicateTheme(theme.theme_id)}
                        className="h-8 w-8 p-0"
                        title="Duplicate"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setThemeToDelete(theme);
                          setShowDeleteDialog(true);
                        }}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Link to={`/admin/assessment-themes/${theme.theme_id}`}>
                        <Button variant="ghost" size="sm" className="h-8 px-2">
                          Edit <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      
      {/* Create Theme Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Assessment Theme</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Theme Name <span className="text-red-400">*</span>
              </label>
              <Input
                value={formData.theme_name}
                onChange={(e) => setFormData(prev => ({ ...prev, theme_name: e.target.value }))}
                placeholder="e.g., Self Discovery"
                className="rounded-lg"
                data-testid="theme-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Explore who you are and what makes you unique..."
                className="rounded-lg resize-none"
                rows={2}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger className="rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <span className="flex items-center gap-2">
                        <cat.icon className={`w-4 h-4 ${cat.color}`} />
                        {cat.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Audience</label>
                <Select
                  value={formData.audience}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, audience: v }))}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AUDIENCES.map(aud => (
                      <SelectItem key={aud.id} value={aud.id}>
                        {aud.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Topic</label>
                <Select
                  value={formData.topic || 'none'}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, topic: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Select topic..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific topic</SelectItem>
                    {TOPICS.map(topic => (
                      <SelectItem key={topic.id} value={topic.id}>
                        {topic.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_active: v }))}
                />
                <label className="text-sm">Active</label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_default}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_default: v }))}
                />
                <label className="text-sm">Set as Default</label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateDialog(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={createTheme} className="rounded-full bg-primary text-black" data-testid="create-theme-submit">
              Create Theme
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Theme?
            </DialogTitle>
            <DialogDescription>
              This will delete "{themeToDelete?.theme_name}" and all its questions.
              {themeToDelete?.question_count > 0 && (
                <span className="block mt-2 text-yellow-400">
                  Note: If this theme has existing sessions, it will be deactivated instead.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteDialog(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={deleteTheme} variant="destructive" className="rounded-full">
              Delete Theme
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssessmentThemeManager;
